import { useRef, useState, useEffect } from "react";
import {
  PlayIcon,
  PauseIcon,
  CloudArrowDownIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from "@heroicons/react/24/solid";
import { toast } from "react-hot-toast";
import { AudioMetadataType } from "../../db/schema";

interface MediaPlayerProps {
  url: string;
  // A presigned, browser-playable video (the footage proxy). When set it is
  // played as-is instead of resolving `url` through /api/audio/playback-url,
  // and the element is a <video> from the start.
  videoUrl?: string;
  onTimeUpdate?: (time: number) => void;
  audioRef?: React.RefObject<HTMLAudioElement>;
  audioMetadata?: AudioMetadataType | null;
  setAudioControlsRef?: React.Dispatch<
    React.SetStateAction<{
      rewind: (seconds: number) => void;
      fastForward: (seconds: number) => void;
      play: () => void;
      pause: () => void;
    } | null>
  >;
}

function MediaPlayer({
  url,
  videoUrl,
  onTimeUpdate,
  audioRef,
  audioMetadata,
  setAudioControlsRef,
}: MediaPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isVideo, setIsVideo] = useState(false);
  const [playbackUrl, setPlaybackUrl] = useState<string>("");
  const internalAudioRef = useRef<HTMLAudioElement>(null);
  const finalAudioRef = audioRef || internalAudioRef;
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const audioType = audioMetadata?.audioType;

  // S3 buckets are private; fetch a server-issued presigned GET URL for playback.
  // Non-S3 URLs (blob:, server stream proxies) are used as-is.
  useEffect(() => {
    let cancelled = false;

    if (videoUrl) {
      setPlaybackUrl(videoUrl);
      return;
    }
    if (!url) {
      setPlaybackUrl("");
      return;
    }

    const marker = "amazonaws.com/";
    const markerIdx = url.indexOf(marker);
    if (markerIdx === -1) {
      setPlaybackUrl(url);
      return;
    }

    const key = url.slice(markerIdx + marker.length);
    fetch("/api/audio/playback-url", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key, audioMetadata: { audioType } }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => {
        if (!cancelled) setPlaybackUrl(data.url);
      })
      .catch((err) => {
        console.error("Failed to fetch playback URL", err);
        if (!cancelled) setPlaybackUrl("");
      });

    return () => {
      cancelled = true;
    };
  }, [url, videoUrl, audioType]);

  // Check if the URL might be a media file (initially assume based on extension)
  const mightBeVideo =
    Boolean(videoUrl) ||
    (url &&
      (url.toLowerCase().includes(".mp4") ||
        url.toLowerCase().includes("video/mp4")));

  // Detect actual video content based on metadata
  const detectMediaType = () => {
    if (finalAudioRef.current && mightBeVideo) {
      const element = finalAudioRef.current as HTMLVideoElement;
      // If it has video dimensions, it's a video; otherwise it's audio-only
      const hasVideo = element.videoWidth > 0 && element.videoHeight > 0;
      setIsVideo(hasVideo);
    } else {
      setIsVideo(false);
    }
  };

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (finalAudioRef.current) {
      finalAudioRef.current.pause();
      finalAudioRef.current.currentTime = 0;
    }
  }, [url, finalAudioRef]);

  // Set up audio control functions for keyboard shortcuts
  useEffect(() => {
    if (setAudioControlsRef && finalAudioRef.current) {
      const audioElement = finalAudioRef.current;

      const controls = {
        rewind: (seconds: number) => {
          if (audioElement) {
            audioElement.currentTime = Math.max(
              0,
              audioElement.currentTime - seconds
            );
            setCurrentTime(audioElement.currentTime);
            onTimeUpdate?.(audioElement.currentTime);
          }
        },
        fastForward: (seconds: number) => {
          if (audioElement) {
            audioElement.currentTime = Math.min(
              audioElement.duration || 0,
              audioElement.currentTime + seconds
            );
            setCurrentTime(audioElement.currentTime);
            onTimeUpdate?.(audioElement.currentTime);
          }
        },
        play: () => {
          if (audioElement) {
            audioElement.play();
            setIsPlaying(true);
          }
        },
        pause: () => {
          if (audioElement) {
            audioElement.pause();
            setIsPlaying(false);
          }
        },
      };

      setAudioControlsRef(controls);
    }
  }, [setAudioControlsRef, finalAudioRef, onTimeUpdate]);

  // Handle fullscreen functionality
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    if (!videoContainerRef.current || !isVideo) return;

    try {
      if (!document.fullscreenElement) {
        await videoContainerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error("Error toggling fullscreen:", error);
      toast.error("Fullscreen not supported");
    }
  };

  const togglePlayPause = () => {
    if (finalAudioRef.current) {
      if (isPlaying) {
        finalAudioRef.current.pause();
      } else {
        finalAudioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoClick = (e: React.MouseEvent) => {
    // Toggle play/pause when clicking video, but not when clicking on controls
    const target = e.target as HTMLElement;
    const clickedOnControls =
      target.closest(".fullscreen-controls") || target.closest("button");
    if (!clickedOnControls) {
      e.preventDefault();
      togglePlayPause();
    }
  };

  const handleTimeUpdate = () => {
    if (finalAudioRef.current) {
      setCurrentTime(finalAudioRef.current.currentTime);
      onTimeUpdate?.(finalAudioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    // Detect if this is actually a video file based on video dimensions
    detectMediaType();

    const getDuration = function (url, cb) {
      const _player = new Audio(url);
      _player.addEventListener(
        "durationchange",
        function (e) {
          if (this.duration !== Infinity) {
            const duration = this.duration;
            _player.remove();
            cb(duration);
          }
        },
        false
      );
      _player.load();
      _player.currentTime = 24 * 60 * 60;
      // _player.volume = 0;
      // _player.play();
    };

    if (finalAudioRef.current && playbackUrl) {
      getDuration(playbackUrl, function (duration) {
        setDuration(duration);
      });
    }
  };

  const handleDownload = async () => {
    if (!url || isDownloading) return;

    let loadingToastId: string | undefined;

    try {
      setIsDownloading(true);
      // Show loading toast while downloading
      loadingToastId = toast.loading("Downloading audio file...", {
        duration: Infinity,
      });

      // Get the filename from the URL
      const fileName = url.split("/").pop() || "audio-recording.m4a";

      // Instead of directly downloading from S3, proxy the request through your server
      // The server will handle the CORS issues and return the file
      const s3Key = url.split("amazonaws.com/")[1];

      const response = await fetch(`/api/audio/download`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          url: url,
          key: s3Key,
          audioMetadata: audioMetadata,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to download the audio file");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      // Dismiss loading toast
      toast.success("Audio file downloaded successfully", {
        id: loadingToastId,
        duration: 3000,
      });
    } catch (error) {
      console.error("Error downloading file:", error);
      if (loadingToastId) {
        toast.dismiss(loadingToastId);
      }
      toast.error("Failed to download audio file");
    } finally {
      setIsDownloading(false);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = Number(e.target.value);
    if (finalAudioRef.current) {
      finalAudioRef.current.currentTime = time;
      setCurrentTime(time);
      onTimeUpdate?.(time);
    }
  };

  return (
    <div className="bg-base-100 p-3 border-b-2">
      {isVideo ? (
        <div
          ref={videoContainerRef}
          className={
            isFullscreen
              ? "relative flex items-center justify-center h-screen bg-black"
              : "relative mb-3"
          }
        >
          <video
            ref={finalAudioRef as React.RefObject<HTMLVideoElement>}
            src={playbackUrl}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={() => setIsPlaying(false)}
            onPause={() => setIsPlaying(false)}
            onPlay={() => setIsPlaying(true)}
            onClick={handleVideoClick}
            className={
              isFullscreen
                ? "w-full h-full object-contain cursor-pointer"
                : "w-full h-auto rounded-lg bg-black cursor-pointer"
            }
            controls={false}
          />
          <button
            onClick={toggleFullscreen}
            className={`absolute top-2 right-2 bg-black/50 hover:bg-black/70 rounded-lg text-white transition-colors ${
              isFullscreen ? "p-3" : "p-2"
            }`}
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? (
              <ArrowsPointingInIcon className="h-6 w-6" />
            ) : (
              <ArrowsPointingOutIcon className="h-4 w-4" />
            )}
          </button>

          {/* Fullscreen controls */}
          {isFullscreen && (
            <div className="absolute bottom-4 left-4 right-4 bg-black/70 rounded-lg p-4 fullscreen-controls">
              <div className="flex items-center gap-3">
                <button
                  onClick={togglePlayPause}
                  className="h-10 w-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
                >
                  {isPlaying ? (
                    <PauseIcon className="h-5 w-5" />
                  ) : (
                    <PlayIcon className="h-5 w-5 ml-0.5" />
                  )}
                </button>
                <div className="flex-1">
                  <div className="relative">
                    <input
                      type="range"
                      min="0"
                      max={duration}
                      value={currentTime}
                      onChange={handleSeek}
                      className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-white"
                      style={{
                        background: `linear-gradient(to right, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.8) ${
                          (currentTime / duration) * 100
                        }%, rgba(255,255,255,0.2) ${
                          (currentTime / duration) * 100
                        }%, rgba(255,255,255,0.2) 100%)`,
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-sm text-white mt-2">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : mightBeVideo ? (
        // Hidden video element for potential video files that haven't loaded yet
        <video
          ref={finalAudioRef as React.RefObject<HTMLVideoElement>}
          src={playbackUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setIsPlaying(false)}
          onPause={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          className="hidden"
          controls={false}
        />
      ) : (
        <audio
          ref={finalAudioRef}
          src={playbackUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setIsPlaying(false)}
          onPause={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          className="hidden"
        />
      )}
      <div className="flex items-center gap-3">
        <button
          onClick={togglePlayPause}
          className="h-8 w-8 flex items-center justify-center rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-colors"
        >
          {isPlaying ? (
            <PauseIcon className="h-4 w-4" />
          ) : (
            <PlayIcon className="h-4 w-4 ml-0.5" />
          )}
        </button>
        <div className="flex-1 mr-16 md:mr-0">
          <input
            type="range"
            min="0"
            max={duration}
            value={currentTime}
            onChange={handleSeek}
            className="w-full h-1.5 bg-base-300 rounded-lg appearance-none cursor-pointer accent-blue-500"
          />
          <div className="flex justify-between text-xs text-base-content/60 mt-1">
            <span>{formatTime(currentTime)}</span>
            <div className="flex items-center">
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className="inline-flex items-center hover:text-blue-700 dark:hover:text-blue-300 focus:outline-none disabled:opacity-50"
                title="Download audio"
              >
                <CloudArrowDownIcon className="size-4 text-blue-500 dark:text-blue-300 inline-block mr-0.5" />
              </button>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MediaPlayer;
